// =============================================================================
// VandieZ admin-web -- Vehicle approval detail (/vehicles/:vehicleId)
// =============================================================================
// Renders identity + capacity + cargo dims + photos + KYC docs + owner
// snapshot. Two action buttons at the bottom: Approve (status -> active)
// and Reject (status -> suspended, notes required).
//
// Photos are public S3-style URLs already on the vehicle row (`all_photos`
// + `primary_photo_url`). KYC documents are auth-gated; fetched as Blobs
// via /kyc/documents/{id}/file and rendered through URL.createObjectURL.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Loader2,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { kycAdminApi, vehicleAdminApi, vehicleModelsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { VehicleCompliancePanel } from "@/components/VehicleCompliancePanel";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  VehicleAdminDecision,
  VehicleAdminDetail,
  VehicleAdminKycDocSnapshot,
  VehicleModelRead,
  VehicleStatus,
} from "@/types/vehicle";

const VEHICLE_STATUS_STYLES: Record<VehicleStatus, string> = {
  pending: "bg-warning-subtle text-warning-subtle-foreground",
  active: "bg-success-subtle text-success-subtle-foreground",
  suspended: "bg-danger-subtle text-danger-subtle-foreground",
  retired: "bg-muted text-muted-foreground",
};

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatBytes(n: number): string {
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

interface DocCardProps {
  doc: VehicleAdminKycDocSnapshot;
}

function DocCard({ doc }: DocCardProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (accessToken === null) {
      return;
    }
    const controller = new AbortController();
    const ref: { revoked: boolean; url: string | null } = {
      revoked: false,
      url: null,
    };
    void (async () => {
      try {
        const blob = await kycAdminApi.documentFile(
          accessToken,
          doc.id,
          controller.signal,
        );
        ref.url = URL.createObjectURL(blob);
        if (!ref.revoked) {
          setPreviewUrl(ref.url);
        }
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Failed to load preview.";
        setPreviewError(msg);
      }
    })();
    return () => {
      ref.revoked = true;
      controller.abort();
      if (ref.url !== null) {
        URL.revokeObjectURL(ref.url);
      }
    };
  }, [accessToken, doc.id]);

  const isImage = doc.mime_type.startsWith("image/");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base capitalize">
          {doc.document_type.replace(/_/g, " ")}
        </CardTitle>
        <CardDescription>
          {formatBytes(doc.file_size_bytes)} · {doc.mime_type} · status:{" "}
          <span className="font-mono">{doc.admin_review_status}</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border bg-muted/40 p-2">
          {previewError !== null ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              {previewError}
            </div>
          ) : previewUrl === null ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : isImage ? (
            <a href={previewUrl} target="_blank" rel="noreferrer">
              <img
                src={previewUrl}
                alt={doc.document_type}
                className="max-h-80 w-full rounded object-contain"
              />
            </a>
          ) : (
            <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>{doc.mime_type} — not previewable inline.</span>
              <a
                className="text-primary underline"
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open file in new tab
              </a>
            </div>
          )}
        </div>
        {doc.document_number_masked !== null && (
          <div className="mt-3 text-sm">
            <span className="text-muted-foreground">Document number: </span>
            <span className="font-mono">{doc.document_number_masked}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Catalog-model combobox -- mirrors components/UserPicker.tsx's debounce-free
// "fetch once, filter client-side" shape (the catalog is small; no need for
// a server round-trip per keystroke like the users search does).
// =============================================================================
interface ModelPickerProps {
  id: string;
  options: VehicleModelRead[];
  query: string;
  onQueryChange: (q: string) => void;
  onSelect: (model: VehicleModelRead) => void;
  disabled?: boolean;
}

function ModelPicker({
  id,
  options,
  query,
  onQueryChange,
  onSelect,
  disabled,
}: ModelPickerProps): React.JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onClick = (e: MouseEvent): void => {
      if (
        wrapperRef.current !== null &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const trimmed = query.trim().toLowerCase();
  const filtered =
    trimmed === ""
      ? options
      : options.filter((m) => m.display_name.toLowerCase().includes(trimmed));

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        id={id}
        value={query}
        onFocus={() => { setOpen(true); }}
        onChange={(e) => {
          onQueryChange(e.target.value);
          setOpen(true);
        }}
        placeholder="Search the catalog…"
        autoComplete="off"
        disabled={disabled}
      />
      {open && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border bg-popover shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              No matching catalog models.
            </div>
          ) : (
            <ul className="divide-y">
              {filtered.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                    onClick={() => {
                      onSelect(m);
                      setOpen(false);
                    }}
                  >
                    {m.display_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

interface PhotoGridProps {
  primary: string | null;
  all: string[];
}

function PhotoGrid({ primary, all }: PhotoGridProps): React.JSX.Element {
  if (all.length === 0 && primary === null) {
    return (
      <div className="text-sm text-muted-foreground">No photos on file.</div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {all.map((url) => {
        const isPrimary = url === primary;
        return (
          <a
            key={url}
            href={url}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "relative block overflow-hidden rounded-md border",
              isPrimary && "ring-2 ring-primary",
            )}
          >
            <img
              src={url}
              alt="Vehicle photo"
              className="aspect-[4/3] w-full object-cover"
            />
            {isPrimary && (
              <span className="absolute bottom-1 left-1 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                Primary
              </span>
            )}
          </a>
        );
      })}
    </div>
  );
}

export function VehicleDetailPage(): React.JSX.Element {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [detail, setDetail] = useState<VehicleAdminDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDecision, setPendingDecision] =
    useState<VehicleAdminDecision | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // ---- Catalog-model assignment (Task 5) ---------------------------------
  const [modelOptions, setModelOptions] = useState<VehicleModelRead[]>([]);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [modelQuery, setModelQuery] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<VehicleModelRead | null>(
    null,
  );
  const [assigningModel, setAssigningModel] = useState<boolean>(false);
  const [promotingModel, setPromotingModel] = useState<boolean>(false);
  const [clearingModel, setClearingModel] = useState<boolean>(false);
  const [modelActionError, setModelActionError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (accessToken === null) {
      return;
    }
    const controller = new AbortController();
    vehicleModelsAdminApi
      .list(accessToken, {}, controller.signal)
      .then((rows) => { setModelOptions(rows); })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setModelsError(
          e instanceof ApiError
            ? e.problem.detail
            : "Couldn't load the vehicle-model catalog.",
        );
      });
    return () => {
      controller.abort();
    };
  }, [accessToken]);

  const loadDetail = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null || vehicleId === undefined) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await vehicleAdminApi.detail(
          accessToken,
          vehicleId,
          signal,
        );
        setDetail(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError
            ? e.problem.detail
            : "Unexpected error. Try again.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, vehicleId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadDetail(controller.signal);
    return () => {
      controller.abort();
    };
  }, [loadDetail]);

  const startDecision = (decision: VehicleAdminDecision) => {
    setPendingDecision(decision);
    setNotes("");
    setSubmitError(null);
  };
  const cancelDecision = () => {
    setPendingDecision(null);
    setNotes("");
    setSubmitError(null);
  };

  const submit = async () => {
    if (
      accessToken === null ||
      vehicleId === undefined ||
      pendingDecision === null
    ) {
      return;
    }
    const trimmed = notes.trim();
    if (pendingDecision === "rejected" && trimmed.length === 0) {
      setSubmitError("Notes are required when rejecting a vehicle.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await vehicleAdminApi.decide(accessToken, vehicleId, {
        decision: pendingDecision,
        ...(trimmed.length > 0 ? { notes: trimmed } : {}),
      });
      setFlash(
        res.decision === "approved"
          ? "Vehicle approved — owner can now toggle online_for_bidding."
          : "Vehicle rejected — owner sees the suspended status.",
      );
      setDetail((prev) =>
        prev === null ? prev : { ...prev, status: res.vehicle_status },
      );
      setPendingDecision(null);
      setNotes("");
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError
          ? e.problem.detail
          : "Unexpected error. Try again.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const saveModel = async () => {
    if (
      accessToken === null ||
      vehicleId === undefined ||
      selectedModel === null
    ) {
      return;
    }
    setAssigningModel(true);
    setModelActionError(null);
    try {
      const res = await vehicleAdminApi.assignModel(accessToken, vehicleId, {
        vehicle_model_id: selectedModel.id,
      });
      setDetail((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              vehicle_model_id: res.vehicle_model_id,
              vehicle_model_display_name: res.vehicle_model_display_name,
            },
      );
      setSelectedModel(null);
      setModelQuery("");
    } catch (e: unknown) {
      setModelActionError(
        e instanceof ApiError
          ? e.problem.detail
          : "Couldn't assign the model. Try again.",
      );
    } finally {
      setAssigningModel(false);
    }
  };

  const clearModel = async () => {
    if (accessToken === null || vehicleId === undefined) {
      return;
    }
    setClearingModel(true);
    setModelActionError(null);
    try {
      const res = await vehicleAdminApi.assignModel(accessToken, vehicleId, {
        vehicle_model_id: null,
      });
      setDetail((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              vehicle_model_id: res.vehicle_model_id,
              vehicle_model_display_name: res.vehicle_model_display_name,
            },
      );
    } catch (e: unknown) {
      setModelActionError(
        e instanceof ApiError
          ? e.problem.detail
          : "Couldn't clear the catalog assignment. Try again.",
      );
    } finally {
      setClearingModel(false);
    }
  };

  const promoteToCatalog = async () => {
    if (accessToken === null || vehicleId === undefined || detail === null) {
      return;
    }
    setPromotingModel(true);
    setModelActionError(null);
    try {
      const created = await vehicleModelsAdminApi.create(accessToken, {
        make: detail.make,
        model: detail.model,
        display_name: `${detail.make} ${detail.model}`,
        default_body_type: detail.vehicle_type,
        typical_payload_kg: detail.payload_capacity_kg,
      });
      setModelOptions((prev) => [...prev, created]);
      const res = await vehicleAdminApi.assignModel(accessToken, vehicleId, {
        vehicle_model_id: created.id,
      });
      setDetail((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              vehicle_model_id: res.vehicle_model_id,
              vehicle_model_display_name: res.vehicle_model_display_name,
            },
      );
    } catch (e: unknown) {
      setModelActionError(
        e instanceof ApiError
          ? e.problem.detail
          : "Couldn't promote this vehicle into the catalog.",
      );
    } finally {
      setPromotingModel(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Vehicle review"
        description="Approve or reject the submitted vehicle. Documents + photos render below."
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { navigate("/vehicles/queue"); }}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Queue
          </Button>
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {flash !== null && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Decision applied</AlertTitle>
            <AlertDescription>{flash}</AlertDescription>
          </Alert>
        )}

        {loading && detail === null ? (
          <div className="space-y-4">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-2/3" />
          </div>
        ) : detail !== null ? (
          <>
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <CardTitle className="font-mono">
                      {detail.registration_number}
                    </CardTitle>
                    <CardDescription>
                      {detail.make} {detail.model} · {detail.year_of_manufacture} ·{" "}
                      {detail.vehicle_type} · {detail.fuel_type}
                    </CardDescription>
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                      VEHICLE_STATUS_STYLES[detail.status],
                    )}
                  >
                    {detail.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <dl className="grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Payload capacity</dt>
                    <dd>{detail.payload_capacity_kg} kg</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Vahan</dt>
                    <dd>
                      {detail.vahan_verified ? "Verified" : "Not verified"}
                    </dd>
                  </div>
                  {detail.cargo_bay_length_ft !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Cargo bay (L×W×H)</dt>
                      <dd>
                        {detail.cargo_bay_length_ft} ×{" "}
                        {detail.cargo_bay_width_ft ?? "?"} ×{" "}
                        {detail.cargo_bay_height_ft ?? "?"} ft
                      </dd>
                    </div>
                  )}
                  {detail.insurance_expiry !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Insurance expiry</dt>
                      <dd>{detail.insurance_expiry}</dd>
                    </div>
                  )}
                  {detail.fitness_expiry !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Fitness expiry</dt>
                      <dd>{detail.fitness_expiry}</dd>
                    </div>
                  )}
                  {detail.permit_expiry !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Permit expiry</dt>
                      <dd>{detail.permit_expiry}</dd>
                    </div>
                  )}
                  {detail.puc_expiry !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">PUC expiry</dt>
                      <dd>{detail.puc_expiry}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Created</dt>
                    <dd className="text-muted-foreground">
                      {formatDateTime(detail.created_at)}
                    </dd>
                  </div>
                </dl>

                {accessToken !== null ? (
                  <div className="mt-4">
                    <VehicleCompliancePanel
                      vehicleId={detail.id}
                      accessToken={accessToken}
                      vehicle={detail}
                      onChanged={() => { void loadDetail(); }}
                    />
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Catalog model</CardTitle>
                <CardDescription>
                  {detail.vehicle_model_id !== null
                    ? "Assigned from the curated vehicle_models catalog."
                    : "This vehicle's make/model isn't in the catalog yet."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {detail.vehicle_model_id !== null ? (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                    <span className="font-medium">
                      {detail.vehicle_model_display_name ?? "Unknown model"}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">
                        Free text on file: {detail.make} {detail.model}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void clearModel()}
                        disabled={clearingModel}
                      >
                        {clearingModel ? (
                          <>
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />{" "}
                            Clearing…
                          </>
                        ) : (
                          "Clear assignment"
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Driver entered{" "}
                      <span className="font-medium">
                        {detail.make} {detail.model}
                      </span>{" "}
                      — not in catalog.
                    </AlertDescription>
                  </Alert>
                )}

                {modelsError !== null && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{modelsError}</AlertDescription>
                  </Alert>
                )}

                <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div>
                    <Label htmlFor="vehicle-model-picker" className="text-xs">
                      Vehicle model
                    </Label>
                    <div className="mt-1">
                      <ModelPicker
                        id="vehicle-model-picker"
                        options={modelOptions}
                        query={modelQuery}
                        onQueryChange={(q) => {
                          setModelQuery(q);
                          setSelectedModel(null);
                        }}
                        onSelect={(m) => {
                          setSelectedModel(m);
                          setModelQuery(m.display_name);
                          setModelActionError(null);
                        }}
                        disabled={assigningModel}
                      />
                    </div>
                  </div>
                  <Button
                    onClick={() => void saveModel()}
                    disabled={selectedModel === null || assigningModel}
                  >
                    {assigningModel ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />{" "}
                        Saving…
                      </>
                    ) : (
                      "Save model"
                    )}
                  </Button>
                </div>

                {detail.vehicle_model_id === null && (
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void promoteToCatalog()}
                      disabled={promotingModel}
                    >
                      {promotingModel ? (
                        <>
                          <Loader2 className="mr-1 h-4 w-4 animate-spin" />{" "}
                          Promoting…
                        </>
                      ) : (
                        `Promote "${detail.make} ${detail.model}" into the catalog`
                      )}
                    </Button>
                  </div>
                )}

                {modelActionError !== null && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{modelActionError}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Owner</CardTitle>
                <CardDescription>
                  {detail.owner_name} · <span className="font-mono">{detail.owner_phone}</span> ·{" "}
                  KYC tier <span className="font-mono">{detail.owner_kyc_tier}</span>
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Photos</CardTitle>
                <CardDescription>
                  {detail.all_photos.length} on file. Primary is highlighted.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PhotoGrid
                  primary={detail.primary_photo_url}
                  all={detail.all_photos}
                />
              </CardContent>
            </Card>

            {detail.documents.length === 0 ? (
              <Card>
                <CardContent className="py-6 text-center text-muted-foreground">
                  No KYC documents linked to this vehicle.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {detail.documents.map((d) => (
                  <DocCard key={d.id} doc={d} />
                ))}
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Decision</CardTitle>
                <CardDescription>
                  {detail.status === "pending"
                    ? "Choose an action for this submission."
                    : `Vehicle is already ${detail.status}. Re-decisions are not supported.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {detail.status !== "pending" ? null : pendingDecision === null ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="default"
                      onClick={() => { startDecision("approved"); }}
                    >
                      <Check className="mr-1 h-4 w-4" /> Approve
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => { startDecision("rejected"); }}
                    >
                      <XCircle className="mr-1 h-4 w-4" /> Reject
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 rounded-md border bg-background p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium capitalize">
                        Confirm: {pendingDecision}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelDecision}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                    </div>
                    <div>
                      <Label htmlFor="vehicle-decision-notes" className="text-xs">
                        Notes{" "}
                        {pendingDecision === "approved"
                          ? "(optional internal note)"
                          : "(required — the owner sees this)"}
                      </Label>
                      <Textarea
                        id="vehicle-decision-notes"
                        value={notes}
                        onChange={(e) => { setNotes(e.target.value); }}
                        placeholder={
                          pendingDecision === "approved"
                            ? "Optional internal note"
                            : "Tell the owner what to fix"
                        }
                        maxLength={1000}
                        disabled={submitting}
                      />
                    </div>
                    {submitError !== null && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{submitError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="flex justify-end gap-2">
                      <Button
                        variant={
                          pendingDecision === "rejected"
                            ? "destructive"
                            : "default"
                        }
                        onClick={() => void submit()}
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />{" "}
                            Submitting…
                          </>
                        ) : (
                          "Confirm"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
    </div>
  );
}
